import "./CaseEditForm.css";

import classNames from "classnames";
import { formatRelative, subHours } from "date-fns";
import { Field, Form, Formik } from "formik";
import { DateTime } from "luxon";

import { Case, CaseAdmin, CliaCertificate, Patient } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { HTML_DATETIME_LOCAL_INPUT_FORMAT } from "shared/utils/dates";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import FlatPatientField from "./FlatPatientField";
import FormikContext from "./FormikContext";
import { getRecentStatusLog } from "../casesTable/CasesTable";
import {
  AdminFieldAssignment,
  AdminFieldGrossedByCert,
  AdminFieldLab,
  AdminFieldSignature,
} from "../edit/fields";
import { CliaCertificateOption, Option } from "../edit/ResourceOptions";

type CaseEditFormProps = {
  adminCase: CaseAdminDetail;
  onSubmit: any;
  setValues: Function;
  isEdited: boolean;
  setEdited: Function;
  canUpdateCase: boolean;
};

const getExpectedStatusByDate = (
  status?: string,
  statusLog?: any
): Date | undefined => {
  return status === "working" || status === "active"
    ? statusLog?.status === status
      ? statusLog?.expected_next_status_by
        ? new Date(statusLog.expected_next_status_by)
        : subHours(
            statusLog?.createdAt ? new Date(statusLog.created_at) : new Date(),
            status === "working" ? -72 : -48
          )
      : subHours(new Date(), status === "working" ? -72 : -48)
    : undefined;
};

const CaseEditForm = ({
  adminCase,
  onSubmit,
  setValues,
  isEdited,
  setEdited,
  canUpdateCase,
}: CaseEditFormProps) => {
  const currentStatusLog = getRecentStatusLog(adminCase.status_log || []);
  const canUpdateAccession =
    // PW cases shouldn't be directly accession-number editable, _unless_ it's a UVD case
    adminCase?.source !== Case.source.PW ||
    adminCase.patient?.clinic?.name?.includes("Utah Valley Dermatology");
  const externalLabs = useAppSelector((state) => state.externalLabs);
  const externalGrossingLab = useAppSelector((state) =>
    selectFeatureFlag(state, "externalGrossingLab")
  );
  return (
    <Formik
      initialValues={{
        ...adminCase,
        expected_next_status_by: getExpectedStatusByDate(
          adminCase.status,
          currentStatusLog
        ),
        billing_components:
          adminCase.source === CaseAdmin.source.PW
            ? adminCase.bill_tc
              ? adminCase.bill_pc
                ? "none"
                : "tc_only"
              : "pc_only"
            : undefined,
        collected_date:
          adminCase.collected_date &&
          DateTime.fromISO(adminCase.collected_date, {
            zone: adminCase?.collected_date_timezone || "UTC",
          }).toFormat(HTML_DATETIME_LOCAL_INPUT_FORMAT),
        received_date:
          adminCase.received_date &&
          DateTime.fromISO(adminCase.received_date, {
            zone: adminCase?.received_date_timezone || "UTC",
          }).toFormat(HTML_DATETIME_LOCAL_INPUT_FORMAT),
      }}
      onSubmit={onSubmit}
      key={`${adminCase.accession}`}
    >
      {({ handleChange, resetForm }) => {
        return (
          <Form className="panelContainer">
            <FormikContext setValues={setValues} />
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Case Details
              </legend>
              <div className="panelContainer__inputGroup">
                <Field name="accession">
                  {({ field }: any) => (
                    <TextInput
                      label="Accession"
                      id="accession"
                      {...field}
                      onChange={(e: any) => {
                        handleChange(e);
                        setEdited(true);
                      }}
                      disabled={!canUpdateAccession || !canUpdateCase}
                    />
                  )}
                </Field>
                {adminCase.source === CaseAdmin.source.PW && (
                  <Field name="external_accession">
                    {({ field }: any) => (
                      <TextInput
                        label="External Accession"
                        id="external_accession"
                        {...field}
                        onChange={(e: any) => {
                          handleChange(e);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                )}
                {adminCase.source === Case.source.PW &&
                  externalGrossingLab &&
                  adminCase.accessioning_lab?.name === "PW Florida" && (
                    <Field name="grossed_by_external_lab_id">
                      {({ field }: any) => (
                        <div className="field--minSize">
                          <SelectInput
                            label="Ext. Grossing Lab"
                            // TODO: imo `id` (and thus htmlFor for the label)
                            // ... should be implied through `name`
                            // (perhaps prefixed or otherwise namespaced or scaoped)
                            // Without duplicating this "name", label has no for
                            id="grossed_by_external_lab_id"
                            options={Object.values(externalLabs).map(
                              (externalLab) => ({
                                label: externalLab.name,
                                value: externalLab.id,
                              })
                            )}
                            {...field}
                            onChange={(e: any) => {
                              handleChange(e);
                              setEdited(true);
                            }}
                            disabled={!canUpdateCase}
                          />
                        </div>
                      )}
                    </Field>
                  )}
                <Field name="priority">
                  {({ field }: any) => (
                    <div className="field--minSize">
                      <SelectInput
                        label="Priority"
                        options={Object.values(CaseAdmin.priority).map((p) => ({
                          label: p,
                          value: p,
                        }))}
                        {...field}
                        onChange={(e: any) => {
                          handleChange(e);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    </div>
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="accessioning_lab">
                  {({ field, form }: any) => (
                    <div className="field__input--minSize">
                      <AdminFieldLab
                        {...field}
                        label="Accessioning Lab"
                        value={field.value}
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            "accessioning_lab",
                            newValue.object
                          );
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    </div>
                  )}
                </Field>
                <Field name="grossed_by_cert">
                  {({ field, form }: any) => (
                    <div className="field__input--minSize">
                      {adminCase.grossed_by_cert ? (
                        <div>
                          <label>TC CLIA</label>
                          <CliaCertificateOption
                            object={
                              adminCase.grossed_by_cert as CliaCertificate
                            }
                          />
                        </div>
                      ) : (
                        <AdminFieldGrossedByCert
                          {...field}
                          labName={adminCase.accessioning_lab?.name}
                          value={field.value}
                          onChange={(newValue: any) => {
                            form.setFieldValue(
                              "grossed_by_cert",
                              newValue.object
                            );
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                        />
                      )}
                    </div>
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="collected_date">
                  {({ field, form }: any) => (
                    <CustomInput label="Collected Date">
                      <input
                        type="datetime-local"
                        label="Collected Date"
                        id="collected_date"
                        {...field}
                        onChange={(e: any) => {
                          form.setFieldValue("collected_date", e.target.value);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    </CustomInput>
                  )}
                </Field>
                <CustomInput label="Collected Date Timezone">
                  {adminCase?.collected_date_timezone || "UTC"}
                </CustomInput>
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="received_date">
                  {({ field, form }: any) => (
                    <CustomInput label="Received Date">
                      <input
                        type="datetime-local"
                        label="Received Date"
                        id="received_date"
                        {...field}
                        onChange={(e: any) => {
                          form.setFieldValue("received_date", e.target.value);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    </CustomInput>
                  )}
                </Field>
                <CustomInput label="Received Date Timezone">
                  {adminCase?.received_date_timezone || "UTC"}
                </CustomInput>
              </div>
              <div className="panelContainer__inputGroup">
                <div className="field__input--minSize">
                  <FlatPatientField patient={adminCase.patient as Patient} />
                </div>
              </div>
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Workflow
              </legend>
              <div className="panelContainer__inputGroup">
                {adminCase.source === CaseAdmin.source.PW && (
                  <Field name="finalizing_signature">
                    {({ field, form }: any) => (
                      <AdminFieldSignature
                        {...field}
                        field="finalizing_signature"
                        label="Finalizing Signature"
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            "finalizing_signature",
                            newValue.object
                          );

                          if (form.values.is_screening) {
                            form.setFieldValue(
                              "finalize_assignment",
                              newValue.object.user
                            );
                          } else {
                            form.setFieldValue(
                              "active_assignment",
                              newValue.object.user
                            );
                          }
                          setEdited(true);
                        }}
                        useIsMissingInfo
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                )}
                {adminCase.source === CaseAdmin.source.PW ? (
                  <Field name="active_assignment">
                    {({ field }: any) => {
                      if (!field.value) return null;
                      const { id, first_name, last_name } = field.value;
                      return (
                        <CustomInput label="Active Assignment">
                          <Option
                            index={id || ""}
                            info={{
                              main: `${first_name} ${last_name}`,
                            }}
                          />
                        </CustomInput>
                      );
                    }}
                  </Field>
                ) : (
                  <Field name="active_assignment">
                    {({ field, form }: any) => (
                      <AdminFieldAssignment
                        {...field}
                        field="active_assignment"
                        label="Active Assignment"
                        onChange={(newValue: any) => {
                          form.setFieldValue(
                            "active_assignment",
                            newValue.object
                          );
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                        useIsMissingInfo
                      />
                    )}
                  </Field>
                )}
                <Field name="is_screening">
                  {({ field, form }: any) => (
                    <CustomInput label="Properties">
                      <div className="panelContainer__inputGroup">
                        <label>
                          <input
                            {...field}
                            type="checkbox"
                            checked={field.value}
                            onChange={() => {
                              form.setFieldValue("is_screening", !field.value);
                              setEdited(true);
                            }}
                            disabled={!canUpdateCase}
                          />
                          &nbsp;Is Screening
                        </label>
                      </div>
                    </CustomInput>
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="screen_assignment">
                  {({ field, form }: any) => (
                    <AdminFieldAssignment
                      {...field}
                      field="screen_assignment"
                      label="Screen Assignment"
                      onChange={(newValue: any) => {
                        form.setFieldValue(
                          "screen_assignment",
                          newValue.object
                        );
                        if (form.values.is_screening) {
                          form.setFieldValue(
                            "active_assignment",
                            newValue.object
                          );
                        }
                        setEdited(true);
                      }}
                      disabled={!canUpdateCase}
                    />
                  )}
                </Field>
                <Field name="finalize_assignment">
                  {({ field, form }: any) => (
                    <AdminFieldAssignment
                      {...field}
                      field="finalize_assignment"
                      label="Finalize Assignment"
                      onChange={(newValue: any) => {
                        form.setFieldValue(
                          "finalize_assignment",
                          newValue.object
                        );
                        setEdited(true);
                      }}
                      disabled={!canUpdateCase}
                    />
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                <Field name="status">
                  {({ field, form }: any) => (
                    <div className="field--minSize">
                      <SelectInput
                        label="Status"
                        options={["active", "working", "finalized"].map(
                          (p) => ({
                            label: p,
                            value: p,
                          })
                        )}
                        {...field}
                        onChange={(e: any) => {
                          form.setValues({
                            ...form.values,
                            status: e.target.value,
                            expected_next_status_by: getExpectedStatusByDate(
                              e.target.value,
                              currentStatusLog
                            ),
                          });

                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    </div>
                  )}
                </Field>
                <div className="panelContainer__inputGroup">
                  <Field name="expected_next_status_by">
                    {({ field, form }: any) => {
                      if (!field.value) {
                        return <></>;
                      }
                      const expected = new Date(field.value);
                      const now = new Date();
                      return (
                        <>
                          <CustomInput label="Expected Next Status">
                            <span
                              className={classNames(" field__expectedDate", {
                                "field__expectedDate--overdue": expected < now,
                              })}
                            >
                              {formatRelative(
                                new Date(field.value),
                                new Date()
                              )}
                            </span>
                          </CustomInput>

                          <CustomInput label="Add/Subtract Hours">
                            <div className="field__expectedDate--actions">
                              <PathologyWatchButton
                                onClick={() => {
                                  const newTime = subHours(
                                    new Date(field.value),
                                    8
                                  );
                                  form.setFieldValue(
                                    "expected_next_status_by",
                                    newTime
                                  );
                                  setEdited(true);
                                }}
                                disabled={!canUpdateCase}
                              >
                                - 8 Hours
                              </PathologyWatchButton>
                              <PathologyWatchButton
                                onClick={() => {
                                  const newTime = subHours(
                                    new Date(field.value),
                                    -8
                                  );
                                  form.setFieldValue(
                                    "expected_next_status_by",
                                    newTime
                                  );
                                  setEdited(true);
                                }}
                                disabled={!canUpdateCase}
                              >
                                + 8 Hours
                              </PathologyWatchButton>
                            </div>
                          </CustomInput>
                        </>
                      );
                    }}
                  </Field>
                </div>
              </div>
            </fieldset>
            {adminCase.source === CaseAdmin.source.PW && (
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Billing Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="billing_type">
                    {({ field }: any) => (
                      <SelectInput
                        label="Billing Type"
                        options={[
                          {
                            label: "Third-Party Bill",
                            value: CaseAdmin.billing_type.GLOBAL,
                          },
                          {
                            label: "Client Bill",
                            value: CaseAdmin.billing_type.CLIENT,
                          },
                          {
                            label: "Cash Pay",
                            value: CaseAdmin.billing_type.CASH,
                          },
                          {
                            label: "No Bill",
                            value: CaseAdmin.billing_type.NONE,
                          },
                        ].map((p) => ({
                          label: p.label,
                          value: p.value,
                        }))}
                        {...field}
                        onChange={(e: any) => {
                          handleChange(e);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                  <Field name="billing_components">
                    {({ field, form }: any) => (
                      <CustomInput label="Billing Component">
                        <div
                          className="panelContainer__inputGroup"
                          style={{ flex: "1" }}
                        >
                          <span className="panelContainer__checkbox">
                            <input
                              type="radio"
                              name="billing_component"
                              id="tc_only"
                              value="tc_only"
                              checked={field.value === "tc_only"}
                              onClick={() => {
                                form.setFieldValue(
                                  "billing_components",
                                  field.value === "tc_only" ? "none" : "tc_only"
                                );
                                setEdited(true);
                              }}
                              onChange={() => {}}
                              disabled={!canUpdateCase}
                            />
                            <label htmlFor="tc_only">TC Only</label>
                          </span>
                          <span className="panelContainer__checkbox">
                            <input
                              type="radio"
                              name="billing_component"
                              id="pc_only"
                              value="pc_only"
                              checked={field.value === "pc_only"}
                              onClick={() => {
                                form.setFieldValue(
                                  "billing_components",
                                  field.value === "pc_only" ? "none" : "pc_only"
                                );
                                setEdited(true);
                              }}
                              onChange={() => {}}
                              disabled={!canUpdateCase}
                            />
                            <label htmlFor="pc_only">PC Only</label>
                          </span>
                        </div>
                      </CustomInput>
                    )}
                  </Field>
                </div>
              </fieldset>
            )}
            <div className="form__actionContainer">
              <PathologyWatchButton
                title="Discard changes to case"
                color="red"
                onClick={() => {
                  resetForm();
                  setEdited(false);
                }}
                disabled={!isEdited}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                title="Save case"
                disabled={!isEdited || !canUpdateCase}
                color="green"
              >
                Save Changes
              </PathologyWatchButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CaseEditForm;
